import React, { useRef, useState, useEffect, useMemo } from 'react';

import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import DetailModal from '@/components/DetailModal';
import { useTranslation } from 'react-i18next';
import { Spin, App, Button, Flex, Tag, notification, Descriptions, DescriptionsProps, Input, Form } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { lotProLocList, orderConfirm, orderDescUpdate, orderFetch, orderItemAdds, orderItemRms, orderPreOut } from '@/apis/inventoryv2';
import { OrderV2, OrderItemV2, OrderItemLocation, InventoryWithLPLParam, InventoryWithLPL, OrderAddItemV2, WLocation, SpecialHandlingConfirm } from '@/types/inventoryV2';
import { TableProps } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { AnyObject } from 'antd/lib/_util/type';
import SText from '@/styles/SText';
import { InboundContentWrap as OutboundContentWrap } from '../InboundOrderForm/style';
import PreviewLocationCode from '@/pages/Warehouse/components/PreviewLocationCode';
import { getLocationIdx } from '@/utils/wLocation';
import { ChangeEnum, OperateEnum } from '@/types/inventory';
import { Product } from '@/types/product';
import convertTo2DArray from '@/utils/convert2double';
import FixOutBoundItems from './FixOutBoundItems';
import WMSAvatar from '@/styles/WMSAvatar';
import { getUserAvatarTip } from '@/utils/user';
import { dateFormat } from '@/utils/format';
import dayjs from 'dayjs';
import { changeTypes, opTypes } from '@/pages/Warehouse/utils';
import useOrderPreOutLocationTypes from '@/hooks/useOrderPreOutLocationTypes';
import { SourceTypeEnum } from '@/types/common';
import OrderConfirmInspection from '@/components/OrderConfirmInspection';
import errorCodes from '@/apis/error';

interface InboundOrderFormProps {
  sourceId: string;
  onCancel: () => void
  noModal?: boolean;
  isReturn?: boolean;
}


const OutboundOrderForm: React.FC<InboundOrderFormProps> = ({ sourceId, onCancel, noModal = false, isReturn = false }) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const selectPreOutLocationTypes = useOrderPreOutLocationTypes();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [orderData, setOrderData] = useState<OrderV2 | null>(null);
  const [isSaleOut, setIsSaleOut] = useState<boolean>(false);
  const [isShipOut, setIsShipOut] = useState<boolean>(false);
  // const [prefitRes, setPrefitRes] = useState<Array<InventoryWithLPL>>([]);
  const [errorItems, setErrorItems] = useState<Array<OrderItemV2>>([]);
  const [fixingItem, setFixingItem] = useState<OrderItemV2 | null>(null);
  const [openPrefitFix, setOpenPrefitFix] = useState<boolean>(false);
  const [desc, setDesc] = useState<string>('');
  const [openInspection, setOpenInspection] = useState<boolean>(false);
  const [inspectionItems, setInspectionItems] = useState<OrderItemV2[]>([]);
  const opType = opTypes.find(item => item.value === orderData?.opType);

  const orderType = changeTypes.find((item) => item.value === orderData?.type);


  const [previewBarcodes, setPreviewBarcodes] = useState<{
    visible: boolean;
    locations: OrderItemLocation[];
    productName: string;
  }>({
    visible: false,
    locations: [],
    productName: ''
  });

  useEffect(() => {
    if (sourceId) {
      fetchOrderData();
    }
  }, [sourceId]);

  const { prefitData, itemCodeCount } = useMemo(() => {
    let prefitData: Array<InventoryWithLPLParam> = [];
    let itemCodeCount: Record<string, { product: Product, count: number }> = {};
    if (!!orderData?.items) {
      orderData.items.forEach(item => {

        if (itemCodeCount[item.productId]) {
          itemCodeCount[item.productId].count += item.count
        } else itemCodeCount[item.productId] = {
          product: { ...item.product },
          count: item.count
        }

        prefitData.push(...(item.locations || []).map(loc => ({
          productId: item.productId,
          lot: item.lot,
          locationId: loc.locationId
        })));
      })
    }
    return { prefitData, itemCodeCount };
  }, [orderData]);


  const fetchOrderData = async () => {
    setLoading(true);
    try {
      const data = await orderFetch(sourceId);
      setOrderData(data);
      setIsSaleOut(data.opType === OperateEnum.TRANSACTION && data.type === ChangeEnum.PRE_OUT)
      setIsShipOut(data.sourceType === SourceTypeEnum.SHIPMETN);
      setDesc(data.desc)
    } catch (error) {
      console.error('Failed to fetch order data:', error);
      notification.error({ placement: 'top', message: t('warehouse.fetchError') });
    } finally {
      setLoading(false);
    }
  };
  const rePreout = async () => {
    setConfirmLoading(true);
    try {
      const locationTypes = await selectPreOutLocationTypes();
      if (!locationTypes) return;
      let res = await orderPreOut(sourceId, locationTypes);
      setOrderData({ ...res })
      setErrorItems([]);
      notification.success({ placement: 'top', message: t(isSaleOut ? 'order.preOutSucc' : 'warehouse.reMatchSucc') });
    } catch (error: any) {
      if (error?.code === 73007 || error?.code === 73008) {
        if (Array.isArray(error?.extra)) {
          let errCodes = orderData?.items
            ?.filter(item => error.extra.includes(item.productId) || error.extra.includes(item.product?.code))
            ?.map(item => item.product?.code ?? '')
            ?.join(', ')
          notification.error({
            placement: 'top',
            message: t('apiError.error31'),
            description: errCodes,
          });
        } else {
          notification.error({
            placement: 'top',
            message: t(isSaleOut ? 'order.preOutErr' : 'warehouse.reMatchFail'),
            description: t('warehouse.insufficientInventory')
          });
        }
      } else if (error === 72809) {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t(isSaleOut ? 'order.preOutErr' : 'warehouse.reMatchFail'),
          description: t('warehouse.needFix'),
        });
      } else if (error === 73010) {
        notification.error({
          placement: 'top',
          message: t(isSaleOut ? 'order.preOutErr' : 'warehouse.reMatchFail'),
          description: t('warehouse.locFailed'),
        });
      } else if ([73024, 73028].includes(error.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: error?.extra?.productCode
        })
      } else {
        // 其他错误
        notification.error({
          placement: 'top',
          message: errorCodes[error] || t(isSaleOut ? 'order.preOutErr' : 'warehouse.reMatchFail'),
          description: error,
        });
      }
    } finally {
      setConfirmLoading(false);
    }
  }
  // 确认的处理函数：先打开巡检弹窗
  const handleConfirm = () => {
    if ((isSaleOut || isShipOut) && !!orderData?.items.find(i => !!!i.locations.length)) return notification.error({ message: t(isSaleOut ? 'order.needPre' : 'warehouse.needPre') })
    setInspectionItems(orderData?.items || []);
    setOpenInspection(true);
  }

  // 巡检弹窗 onOk 后执行出库确认
  const doConfirm = async (specialHandling: SpecialHandlingConfirm[]) => {
    setConfirmLoading(true);
    try {
      if (orderData && desc) {
        await orderDescUpdate(orderData.id, desc)
      }
      await orderConfirm(sourceId, 0, specialHandling);
      // try {
      //   await orderConfirm(sourceId, 0, specialHandling);
      // } catch (error: any) {
      //   if (error?.code === 70845) await orderConfirm(sourceId, 0);
      //   else throw error
      // }
      notification.success({ placement: 'top', message: t('warehouse.outboundSuccess') });
      onCancel();
    } catch (error: any) {
      if (error?.code === 73007 || error?.code === 73008) {
        if (Array.isArray(error?.extra)) {
          let errCodes = orderData?.items
            ?.filter(item => error.extra.includes(item.productId) || error.extra.includes(item.product?.code))
            ?.map(item => item.product?.code ?? '')
            ?.join(', ')
          notification.error({
            placement: 'top',
            message: t('apiError.error31'),
            description: errCodes,
          });
        } else {
          notification.error({
            placement: 'top',
            message: t('warehouse.outboundError'),
            description: t('warehouse.insufficientInventory')
          });
        }
      } else if (error === 72809) {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t('warehouse.outFail'),
          description: t('warehouse.needFix'),
        });
      } else if (error === 73010) {
        notification.error({
          placement: 'top',
          message: t('warehouse.outFail'),
          description: t('warehouse.locFailed'),
        });
      } else if (error === 73022) {
        // 其他错误：提供强制出库选项
        await modal.confirm({
          title: t('warehouse.outFail'),
          content: t('warehouse.reMatchTip'),
          onOk: rePreout,
          onCancel: () => {
            // 用户取消强制出库
            setConfirmLoading(false);
          }
        });
      } else if ([73024, 73028].includes(error.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: error?.extra?.productCode
        })
      } else if (error.code === 73011) {
        let ids: string[] = (error?.extra ?? []);
        let codes = (orderData?.items ?? []).filter(item => ids.includes(item.productId)).map(p => p.product.code).join(', ')
        notification.error({
          message: t('warehouse.barcodeErr'),
          description: codes
        })
      } else {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t('warehouse.outFail'),
        });
      }
    } finally {
      setConfirmLoading(false);
    }
  }

  const getErrItems = (prefitResParams: InventoryWithLPL[]) => {
    if (orderData) {
      let prefitRes: InventoryWithLPL[] = [...prefitResParams]
      let errDatas: OrderItemV2[] = [];
      for (let i = 0; i < orderData.items.length; i++) {
        const item = orderData.items[i];
        let isErr: boolean = false;
        let itemLocCunt: number = 0;

        for (let j = 0; j < item.locations.length; j++) {
          const loc = item.locations[j];
          itemLocCunt += (loc.count || 0)
          let matchIdx = prefitRes
            .findIndex(prefit => (
              prefit.lotItem.lot.lot === item.lot
              && prefit.lotItem.productId === item.productId
              && prefit.locationId === loc.locationId
            ));
          if (matchIdx === -1) {
            isErr = true;
            break;
          } else {
            let matchCount = prefitRes[matchIdx].count || 0
            if (matchCount < loc.count) {
              isErr = true;
              break;
            } else prefitRes[matchIdx].count -= loc.count
          }

        }
        if (isErr || itemLocCunt !== item.count) errDatas.push({ ...item });
      };
      if (errDatas.length > 0) {
        setErrorItems([...errDatas]);
        notification.error({
          message: t('warehouse.needPrefit'),
          description: errDatas.map(i => i.lot).join(', ')
        })
      } else {
        notification.success({
          message: t('warehouse.needSucc'),
        })
      }
    }
  }

  const prefitOut = async () => {
    setLoading(true);
    let prefitDatas: Array<InventoryWithLPLParam[]> = convertTo2DArray(prefitData, 20);
    let allPrefitRes: Array<InventoryWithLPL> = [];
    try {
      for (let i = 0; i < prefitDatas.length; i++) {
        const data = prefitDatas[i];
        let res = await lotProLocList(data);
        allPrefitRes.push(...res)
      }
      getErrItems([...allPrefitRes])
      // setPrefitRes([...allPrefitRes])
    } catch (error) {
      notification.error({
        message: t('warehouse.needFail'),
      })
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFix = (item: OrderItemV2) => {
    setFixingItem(item);
    setOpenPrefitFix(true);
  }

  const handleFixItem = async (addDatas: OrderAddItemV2[]) => {
    if (!!!orderData || !!!fixingItem) return
    setOpenPrefitFix(false);
    setLoading(true);
    try {
      await orderItemRms(orderData.id, [fixingItem.id])
      let newItems = await orderItemAdds(orderData.id, addDatas);
      setOrderData(oldOrder => {
        let newOrder: OrderV2 = {
          ...oldOrder!,
          items: [...oldOrder!.items.filter(i => i.id !== fixingItem.id), ...newItems]
        };
        return newOrder;
      })
      setErrorItems(last => last.filter(item => item.id !== fixingItem.id));
      notification.success({
        message: t('warehouse.fixSucc')
      })
    } catch (error) {
      notification.error({
        message: t('warehouse.fixFail')
      })
    } finally {
      setLoading(false);
      setFixingItem(null);
    }
  }

  const descriptionItems: DescriptionsProps["items"] = [
    {
      label: t('order.operator'),
      children: orderData && (
        !!!orderData?.updater ? '-' : <WMSAvatar
          uniChar={orderData?.updater?.id ?? "1"}
          avatarId={orderData?.updater?.avatar ?? ""}
          creator={orderData?.updater}
          alt={orderData?.updater ? getUserAvatarTip(orderData?.updater) : ''}
          size={24}
        />
      ),
      span: 1,
    },
    {
      label: t('order.oprtTime'),
      children: orderData && dateFormat(dayjs(orderData?.updatedAt), true),
      span: 1,
    },
    {
      label: t('order.operationSource'),
      span: 1,
      children: orderData && (
        <Tag key={opType?.label} color={opType?.colorKey}>{`${opType?.label ?? ""
          }`}</Tag>
      ),
    },
    {
      label: t('order.orderType'),
      span: 1,
      children: orderData && (
        <Tag key={orderType?.label} color={orderType?.colorKey}>{`${orderType?.label ?? ""
          }`}</Tag>
      ),
    },
    // {
    //   label: t('purchase.remark'),
    //   span: 3,
    //   children: orderData && <Input.TextArea
    //     value={desc}
    //     onChange={e => setDesc(e.target.value)}
    //   />,
    // }
  ];

  const columns: TableProps<OrderItemV2>['columns'] = useMemo(() => (
    [
      {
        title: t('warehouse.batchNo'),
        key: 'lot',
        width: 320,
        fixed: 'left',
        render: (_, record) => {
          let isErr = !!errorItems.find(eItem => eItem.id === record.id);
          return (
            <div style={{ width: "100%", height: "100%", backgroundColor: isErr ? "#ffeeee" : "unset" }}>
              <SText text={record.lot || ''} style={{ color: isErr ? "#CF1322" : "unset" }} />
            </div>
          )
        }
      },
      {
        title: t('warehouse.supplierBatchNo'),
        key: 'thdLot',
        width: 150,
        render: (_, record) => <SText text={record.thdLot || ''} />
      },
      {
        title: t('purchase.productInfo'),
        children: [
          {
            title: <SText text={t('product.code')} />,
            key: 'productCode',
            width: 120,
            render: (_, record) => <SText text={record.product?.code || ''} style={{ width: "100%" }} />
          },
          {
            title: <SText text={t('product.name')} />,
            key: 'productName',
            width: 150,
            render: (_, record) => <SText text={record.product?.name || ''} style={{ width: "100%" }} />
          },
          {
            title: <SText text={t('product.brand1')} />,
            key: 'model',
            width: 120,
            render: (_, record) => <SText text={record.product?.model || ''} style={{ width: "100%" }} />
          },
          {
            title: <SText text={t('product.uom')} />,
            key: 'uom',
            width: 80,
            render: (_, record) => <SText text={record.product?.uom || ''} style={{ width: "100%" }} />
          },
        ]
      },
      {
        title: <SText text={t('warehouse.location')} />,
        key: 'locations',
        width: 220,
        render: (_, record) => {
          if (!record.locations || record.locations.length === 0) {
            return '-';
          }
          return <Flex wrap>
            {record.locations.map(loc => (
              <Tag style={{ marginBottom: 4 }}>{getLocationIdx(loc.location)} ({loc.count})</Tag>
            ))}
          </Flex>
        }
      },
      {
        title: t('order.quantity'),
        dataIndex: 'count',
        key: 'count',
        width: 100,
        fixed: 'right',
        render: (value) => value || 0
      },
      {
        title: t('common.opt'),
        key: 'action',
        fixed: 'right',
        width: 260,
        render: (_, record) => (
          <Flex gap={8}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setPreviewBarcodes({
                  visible: true,
                  locations: record.locations || [],
                  productName: record.product?.name || ''
                });
              }}
            >
              {t('warehouse.viewBarcode')}
            </Button>
            {errorItems.find(it => it.id === record.id) && <Button type="primary" onClick={() => handleOpenFix(record)} >{t('warehouse.fixOut')}</Button>}
          </Flex>
        )
      }
    ]
  ), [errorItems]);

  const headerChildren = (
    <Flex vertical style={{ width: "100%" }} gap={8}>
      <Descriptions bordered items={descriptionItems} column={4} />
      <Form.Item label={t('purchase.remark')} style={{ flex: 1 }}>
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} value={desc} onChange={e => setDesc(e.target.value)} maxLength={100} showCount />
      </Form.Item>
      <Flex gap={8}>
        {
          false && (isSaleOut || isShipOut) ? <>
            <Button type="primary" onClick={rePreout} loading={confirmLoading}>{t('order.preOut')}</Button>
            <Button onClick={prefitOut}>{t('warehouse.outPrefit')}</Button>
          </>
            : <>
              <Button onClick={prefitOut}>{t('warehouse.outPrefit')}</Button>
              <Button type="primary" onClick={rePreout} loading={confirmLoading} disabled={errorItems.length === 0}>{t('warehouse.rePreout')}</Button>
            </>
        }
      </Flex>
    </Flex>
  )

  const Content = (
    <>
      <OutboundContentWrap>
        {
          loading ? <div className="loading-box"><Spin /></div>
            : <STableLayout
              headerChildren={headerChildren}
              total={orderData?.items ? orderData?.items.length : 0}
              tableLayoutRef={tableRef}
              columns={columns as ColumnsType<AnyObject>}
              dataSource={orderData?.items}
              rowKey="id"
              pagination={false}
              extHeight={70}
            />
        }

      </OutboundContentWrap>
      <PreviewLocationCode
        visible={previewBarcodes.visible}
        onClose={() => setPreviewBarcodes({ visible: false, locations: [], productName: '' })}
        locationBarcodes={(previewBarcodes?.locations || []).map(loc => ({
          count: loc.count,
          location: loc.location,
          barcodes: loc.barcodes || []
        }))}
        title={`${t('warehouse.viewBarcode')} - ${previewBarcodes.productName}`}
      />
      {fixingItem && openPrefitFix && <FixOutBoundItems isReturn={isReturn}
        errItems={[fixingItem]}
        open={openPrefitFix}
        onClose={() => setOpenPrefitFix(false)}
        onConfirm={handleFixItem}
      />}
      {/* 巡检弹窗：onOk 后执行出库确认逻辑 */}
      <OrderConfirmInspection
        items={inspectionItems}
        open={openInspection}
        onOk={(data) => {
          setOpenInspection(false);
          doConfirm(data);
        }}
        onClose={() => setOpenInspection(false)}
      />
    </>
  )

  return (
    noModal ? Content
      : <DetailModal noLog
        modalProps={{
          width: 1200,
          title: `${t(isShipOut ? 'warehouse.shipmentOut' : isSaleOut ? 'order.saleOut' : orderData?.type === ChangeEnum.OUT ? 'inventory.outbound' : 'warehouse.pickingOutbound')}  ${orderData?.number || ''}`,
          onCancel,
          footer: (
            <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button key="cancel" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button key="submit" type="primary" onClick={handleConfirm} loading={confirmLoading} disabled={errorItems.length > 0}>
                {t('common.confirm')}
              </Button>
            </div>
          )
        }}
      >
        {Content}
      </DetailModal>
  )
};

export default OutboundOrderForm;
